import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import ContentSearch from './ContentSearch';
import FilterSelect, { FilterOption } from '../FilterSelect';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';

const PREFIX = 'Toolbar';

const classes = {
  container: `${PREFIX}-container`,
  filter: `${PREFIX}-filter`,
  search: `${PREFIX}-search`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },

  [`& .${classes.filter}`]: {
    width: 150,
  },

  [`& .${classes.search}`]: {
    flexGrow: 1,
  },
}));

interface ToolbarProps {
  button?: React.ReactNode;
  secondButton?: React.ReactNode;
  importButton?: React.ReactNode;
  disableImport?: boolean;
  filterOptions?: FilterOption[];
  currentFilter: string;
  searchIcon: React.ReactNode;
  searchPlaceholder: string;
  searchAreaPlaceholder?: string;
  currentSearchString: string;
  currentSearchDriverId?: string;
  currentSearchAreaString?: string;
  onFilterChange: (val: string) => void;
  onSearchChange: (val: string) => void;
  onSearchAreaChange?: (val: string) => void;
  onSearchDriverIdChange?: (val: string) => void;
  onCreate: () => void;
  onSecondCreate?: () => void;
  onImport: (file: File) => void;
}

export default function Toolbar({
  button,
  secondButton,
  importButton,
  disableImport,
  filterOptions,
  currentFilter,
  searchIcon,
  searchPlaceholder,
  searchAreaPlaceholder,
  currentSearchString,
  currentSearchDriverId,
  currentSearchAreaString,
  onFilterChange,
  onSearchChange,
  onSearchAreaChange,
  onSearchDriverIdChange,
  onCreate,
  onSecondCreate,
  onImport,
}: ToolbarProps) {
  const { t } = useTranslation('common');

  const onCreateClicked = (evt: any) => {
    evt.preventDefault();
    onCreate();
  };

  const onSecondCreateClicked = (evt: any) => {
    evt.preventDefault();
    if (onSecondCreate) {
      onSecondCreate();
    }
  };

  const onImportClicked = (evt: any) => {
    evt.preventDefault();
    onImport(evt.target.files[0]);
    evt.target.value = null;
  };

  return (
    <StyledBox className={classes.container}>
      {filterOptions ? (
        <FilterSelect
          filterOptions={filterOptions}
          currentFilter={currentFilter}
          className={classes.filter}
          onFilterChange={onFilterChange}
        />
      ) : null}
      <Box className={classes.search}>
        <ContentSearch
          icon={searchIcon}
          placeholder={searchPlaceholder}
          currentSearchFilter={currentSearchString}
          onSearchChange={onSearchChange}></ContentSearch>
      </Box>
      {onSearchAreaChange ? (
        <Box className={classes.search}>
          <ContentSearch
            icon={searchIcon}
            placeholder={searchAreaPlaceholder}
            currentSearchFilter={currentSearchAreaString}
            onSearchChange={onSearchAreaChange}></ContentSearch>
        </Box>
      ) : null}
      {onSearchDriverIdChange ? (
        <Box className={classes.search}>
          <ContentSearch
            icon={<PersonIcon />}
            placeholder={t('providers.searchDriverId') || ''}
            type="number"
            currentSearchFilter={currentSearchDriverId}
            onSearchChange={onSearchDriverIdChange}></ContentSearch>
        </Box>
      ) : null}
      {button ? (
        <Button variant="contained" color="primary" onClick={onCreateClicked}>
          {button}
        </Button>
      ) : null}
      {secondButton ? (
        <Button
          variant="contained"
          color="primary"
          onClick={onSecondCreateClicked}>
          {secondButton}
        </Button>
      ) : null}
      {importButton ? (
        <>
          <input
            type="file"
            id="importButton"
            name="document"
            hidden
            accept=".csv"
            onChange={onImportClicked}
          />
          <label htmlFor={'importButton'}>
            <Button
              variant="contained"
              color="primary"
              disabled={disableImport}
              component="div">
              {importButton}
            </Button>
          </label>
        </>
      ) : null}
    </StyledBox>
  );
}
